import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
    fetchMeetingViewPatients,
    fetchMeetingViewPreferences,
    fetchPatientById,
    fetchPatients,
    fetchProgressNotesPatients,
    fetchProgressNotesSelectionPatients,
    fetchWeekendDischargePatients,
    fetchUnits,
    saveMeetingViewPreferences,
    savePatientLlosNote,
    savePatientProgressNote,
    savePatientStickyNote
} from './patients-api';
import type {
    AiFeedbackEntry,
    AiFeedbackFieldKey,
    CCTPatient,
    LatestProgressNote,
    MeetingViewMeta,
    MeetingViewPreferences,
    PatientProfile,
    UnitOption
} from '@/types/patient';
import { DEFAULT_ROWS_PER_PAGE } from '@/constant';
import { areMeetingViewColumnIdListsEqual, mergeMeetingViewUiOnlyColumnOrder } from '@/utils/meeting-view/meetingViewColumns';
import { clearProgressNoteSessionCache } from '@/utils/progress-notes/progressNoteSessionCache';
import { logout } from '../auth/logout';
export interface PatientsState {
    patients: CCTPatient[];
    currentPatient: PatientProfile | null;
    units: UnitOption[];
    total: number;
    serverPage: number;
    serverLimit: number;
    loading: boolean;
    progressNotesPatients: CCTPatient[];
    progressNotesTotal: number;
    progressNotesServerPage: number;
    progressNotesServerLimit: number;
    progressNotesLoading: boolean;
    progressNotesError: string | null;
    currentProgressNotesRequestId: string | null;
    progressNotesSelectionPatients: CCTPatient[];
    progressNotesSelectionTotal: number;
    progressNotesSelectionServerPage: number;
    progressNotesSelectionServerLimit: number;
    progressNotesSelectionLoading: boolean;
    progressNotesSelectionError: string | null;
    currentProgressNotesSelectionRequestId: string | null;
    unitsLoading: boolean;
    error: string | null;
    unitsError: string | null;
    currentPatientLoading: boolean;
    currentPatientError: string | null;
    currentPatientsRequestId: string | null;
    meetingViewPatients: CCTPatient[];
    meetingViewTotal: number;
    meetingViewServerPage: number;
    meetingViewServerLimit: number;
    meetingViewMeta: MeetingViewMeta | null;
    meetingViewPreferences: MeetingViewPreferences | null;
    meetingViewLoading: boolean;
    meetingViewError: string | null;
    meetingViewPreferencesLoading: boolean;
    meetingViewPreferencesSaving: boolean;
    meetingViewLocalPreferencesDirty: boolean;
    currentMeetingViewRequestId: string | null;
    currentMeetingViewPreferencesRequestId: string | null;
}

const initialState: PatientsState = {
    patients: [],
    currentPatient: null,
    units: [],
    total: 0,
    serverPage: 1,
    serverLimit: DEFAULT_ROWS_PER_PAGE,
    loading: false,
    progressNotesPatients: [],
    progressNotesTotal: 0,
    progressNotesServerPage: 1,
    progressNotesServerLimit: DEFAULT_ROWS_PER_PAGE,
    progressNotesLoading: false,
    progressNotesError: null,
    currentProgressNotesRequestId: null,
    progressNotesSelectionPatients: [],
    progressNotesSelectionTotal: 0,
    progressNotesSelectionServerPage: 1,
    progressNotesSelectionServerLimit: DEFAULT_ROWS_PER_PAGE,
    progressNotesSelectionLoading: false,
    progressNotesSelectionError: null,
    currentProgressNotesSelectionRequestId: null,
    unitsLoading: false,
    error: null,
    unitsError: null,
    currentPatientLoading: false,
    currentPatientError: null,
    currentPatientsRequestId: null,
    meetingViewPatients: [],
    meetingViewTotal: 0,
    meetingViewServerPage: 1,
    meetingViewServerLimit: DEFAULT_ROWS_PER_PAGE,
    meetingViewMeta: null,
    meetingViewPreferences: null,
    meetingViewLoading: false,
    meetingViewError: null,
    meetingViewPreferencesLoading: false,
    meetingViewPreferencesSaving: false,
    meetingViewLocalPreferencesDirty: false,
    currentMeetingViewRequestId: null,
    currentMeetingViewPreferencesRequestId: null
};

const patientsSlice = createSlice({
    name: 'patients',
    initialState,
    reducers: {
        clearPatients: (state) => {
            state.patients = [];
            state.currentPatient = null;
            state.total = 0;
            state.serverPage = 1;
            state.serverLimit = DEFAULT_ROWS_PER_PAGE;
            state.error = null;
        },
        clearProgressNotes: (state) => {
            state.progressNotesPatients = [];
            state.progressNotesTotal = 0;
            state.progressNotesServerPage = 1;
            state.progressNotesServerLimit = DEFAULT_ROWS_PER_PAGE;
            state.progressNotesError = null;
            clearProgressNoteSessionCache();
        },
        clearProgressNotesError: (state) => {
            state.progressNotesError = null;
        },
        clearProgressNotesSelection: (state) => {
            state.progressNotesSelectionPatients = [];
            state.progressNotesSelectionTotal = 0;
            state.progressNotesSelectionServerPage = 1;
            state.progressNotesSelectionServerLimit = DEFAULT_ROWS_PER_PAGE;
            state.progressNotesSelectionError = null;
        },
        clearProgressNotesSelectionError: (state) => {
            state.progressNotesSelectionError = null;
        },
        patchProgressNotesSelectionPatient: (state, { payload }: PayloadAction<{ patientId: string; isSelected: boolean }>) => {
            const patient = state.progressNotesSelectionPatients.find((item) => item.id === payload.patientId);
            if (patient) {
                patient.isSelected = payload.isSelected;
            }
        },
        patchProgressNotesSelectionPatients: (state, { payload }: PayloadAction<{ patientIds: string[]; isSelected: boolean }>) => {
            const idSet = new Set(payload.patientIds);
            state.progressNotesSelectionPatients.forEach((patient) => {
                if (idSet.has(patient.id)) {
                    patient.isSelected = payload.isSelected;
                }
            });
        },
        patchProgressNotesPatientLatestNote: (
            state,
            { payload }: PayloadAction<{ patientId: string; latestProgressNote: LatestProgressNote }>
        ) => {
            const patient = state.progressNotesPatients.find((item) => item.id === payload.patientId);
            if (patient) {
                patient.latestProgressNote = payload.latestProgressNote;
            }
        },
        clearUnits: (state) => {
            state.units = [];
            state.unitsError = null;
        },
        clearError: (state) => {
            state.error = null;
        },
        clearCurrentPatient: (state) => {
            state.currentPatient = null;
            state.currentPatientError = null;
            state.currentPatientLoading = false;
        },
        clearUnitsError: (state) => {
            state.unitsError = null;
        },
        clearMeetingView: (state) => {
            state.meetingViewPatients = [];
            state.meetingViewTotal = 0;
            state.meetingViewServerPage = 1;
            state.meetingViewServerLimit = DEFAULT_ROWS_PER_PAGE;
            state.meetingViewMeta = null;
            state.meetingViewPreferences = null;
            state.meetingViewError = null;
            state.meetingViewPreferencesSaving = false;
            state.meetingViewLocalPreferencesDirty = false;
            state.currentMeetingViewPreferencesRequestId = null;
        },
        patchMeetingViewVisibleColumns: (state, { payload }: PayloadAction<string[]>) => {
            if (state.meetingViewMeta) {
                state.meetingViewMeta.visibleColumns = payload;
            }
            if (state.meetingViewPreferences) {
                state.meetingViewPreferences.visibleColumns = payload;
            }
            state.meetingViewLocalPreferencesDirty = true;
        },
        patchMeetingViewLosThreshold: (state, { payload }: PayloadAction<{ effectiveLosThreshold: number }>) => {
            if (state.meetingViewMeta) {
                state.meetingViewMeta.effectiveLosThreshold = payload.effectiveLosThreshold;
            }
            if (state.meetingViewPreferences) {
                state.meetingViewPreferences.effectiveLosThreshold = payload.effectiveLosThreshold;
                state.meetingViewPreferences.losThreshold = payload.effectiveLosThreshold;
            }
            state.meetingViewLocalPreferencesDirty = true;
        },
        markMeetingViewPreferencesSynced: (state) => {
            state.meetingViewLocalPreferencesDirty = false;
        },
        /** After weekend handoff submit: reflect flag in list + detail cache. */
        markPatientWeekendDischargeSubmitted: (
            state,
            { payload }: PayloadAction<{ patientId: string; handOffType: 'weekend' | 'general' }>
        ) => {
            const { patientId, handOffType } = payload;
            const inList = state.patients.find((p) => p.id === patientId);
            if (inList) {
                inList.isWeekendDischarge = handOffType === 'weekend';
                inList.nonWeekendHandoff = handOffType === 'general';
            }
            if (state.currentPatient?.patientId === patientId) {
                state.currentPatient.isWeekendDischarge = handOffType === 'weekend';
                state.currentPatient.nonWeekendHandoff = handOffType === 'general';
            }
        },
        /** After patient notes save — keep detail profile in sync without refetch. */
        patchPatientNotes: (state, { payload }: PayloadAction<Array<{ patientId: string; note: string; noteId: string }>>) => {
            payload.forEach(({ patientId, note, noteId }) => {
                if (state.currentPatient?.patientId !== patientId) {
                    return;
                }
                state.currentPatient.patientNote = { id: noteId, note };
            });
        },
        patchPatientAiFeedback: (state, { payload }: PayloadAction<{ aiField: AiFeedbackFieldKey; entry: AiFeedbackEntry }>) => {
            if (!state.currentPatient) {
                return;
            }
            const { aiField, entry } = payload;
            state.currentPatient.aiFeedback = {
                ...state.currentPatient.aiFeedback,
                [aiField]: entry
            };
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPatients.pending, (state, { meta }) => {
                state.loading = true;
                state.error = null;
                state.currentPatientsRequestId = meta.requestId;
            })
            .addCase(fetchPatients.fulfilled, (state, { meta, payload }) => {
                if (state.currentPatientsRequestId !== meta.requestId) {
                    return;
                }
                state.loading = false;
                state.patients = payload.items;
                state.total = payload.total;
                state.serverPage = payload.page;
                state.serverLimit = payload.limit;
                state.error = null;
                state.currentPatientsRequestId = null;
            })
            .addCase(fetchPatients.rejected, (state, { error, meta, payload }) => {
                if (state.currentPatientsRequestId !== meta.requestId) {
                    return;
                }
                state.loading = false;
                if (error.name === 'AbortError') {
                    state.error = null;
                } else {
                    state.error = payload as string;
                }
                state.currentPatientsRequestId = null;
            })
            .addCase(fetchWeekendDischargePatients.pending, (state, { meta }) => {
                state.loading = true;
                state.error = null;
                state.currentPatientsRequestId = meta.requestId;
            })
            .addCase(fetchWeekendDischargePatients.fulfilled, (state, { meta, payload }) => {
                if (state.currentPatientsRequestId !== meta.requestId) {
                    return;
                }
                state.loading = false;
                state.patients = payload.items;
                state.total = payload.total;
                state.serverPage = payload.page;
                state.serverLimit = payload.limit;
                state.error = null;
                state.currentPatientsRequestId = null;
            })
            .addCase(fetchWeekendDischargePatients.rejected, (state, { error, meta, payload }) => {
                if (state.currentPatientsRequestId !== meta.requestId) {
                    return;
                }
                state.loading = false;
                if (error.name === 'AbortError') {
                    state.error = null;
                } else {
                    state.error = payload as string;
                }
                state.currentPatientsRequestId = null;
            })
            .addCase(fetchMeetingViewPatients.pending, (state, { meta }) => {
                state.meetingViewLoading = true;
                state.meetingViewError = null;
                state.currentMeetingViewRequestId = meta.requestId;
            })
            .addCase(fetchMeetingViewPatients.fulfilled, (state, { meta, payload }) => {
                if (state.currentMeetingViewRequestId !== meta.requestId) {
                    return;
                }
                state.meetingViewLoading = false;
                state.meetingViewPatients = payload.items;
                state.meetingViewTotal = payload.total;
                state.meetingViewServerPage = payload.page;
                state.meetingViewServerLimit = payload.limit;
                if (payload.meta) {
                    const keepLocalPreferences = Boolean(
                        state.currentMeetingViewPreferencesRequestId || state.meetingViewLocalPreferencesDirty
                    );
                    state.meetingViewMeta = {
                        ...payload.meta,
                        visibleColumns:
                            keepLocalPreferences && state.meetingViewMeta
                                ? state.meetingViewMeta.visibleColumns
                                : mergeMeetingViewUiOnlyColumnOrder(
                                      payload.meta.visibleColumns,
                                      state.meetingViewMeta?.visibleColumns ?? []
                                  ),
                        effectiveLosThreshold:
                            keepLocalPreferences && state.meetingViewMeta
                                ? state.meetingViewMeta.effectiveLosThreshold
                                : payload.meta.effectiveLosThreshold
                    };
                }
                state.meetingViewError = null;
                state.currentMeetingViewRequestId = null;
            })
            .addCase(fetchMeetingViewPatients.rejected, (state, { error, meta, payload }) => {
                if (state.currentMeetingViewRequestId !== meta.requestId) {
                    return;
                }
                state.meetingViewLoading = false;
                if (error.name === 'AbortError') {
                    state.meetingViewError = null;
                } else {
                    state.meetingViewError = payload as string;
                }
                state.currentMeetingViewRequestId = null;
            })
            .addCase(fetchMeetingViewPreferences.pending, (state) => {
                state.meetingViewPreferencesLoading = true;
            })
            .addCase(fetchMeetingViewPreferences.fulfilled, (state, { payload }) => {
                state.meetingViewPreferencesLoading = false;
                state.meetingViewPreferences = payload;
            })
            .addCase(fetchMeetingViewPreferences.rejected, (state) => {
                state.meetingViewPreferencesLoading = false;
            })
            .addCase(saveMeetingViewPreferences.pending, (state, { meta }) => {
                state.meetingViewPreferencesSaving = true;
                state.currentMeetingViewPreferencesRequestId = meta.requestId;
            })
            .addCase(saveMeetingViewPreferences.fulfilled, (state, { payload, meta }) => {
                if (state.currentMeetingViewPreferencesRequestId !== meta.requestId) {
                    return;
                }
                state.meetingViewPreferencesSaving = false;
                state.currentMeetingViewPreferencesRequestId = null;
                const currentVisibleColumns =
                    state.meetingViewMeta?.visibleColumns ?? state.meetingViewPreferences?.visibleColumns ?? meta.arg.body.visibleColumns;
                const requestedLos = meta.arg.body.losThreshold;
                const currentLos = state.meetingViewMeta?.effectiveLosThreshold ?? state.meetingViewPreferences?.effectiveLosThreshold;
                const losMovedOn = requestedLos !== undefined && currentLos !== undefined && currentLos !== requestedLos;
                const columnsMovedOn = !areMeetingViewColumnIdListsEqual(currentVisibleColumns, meta.arg.body.visibleColumns);

                state.meetingViewPreferences = {
                    ...payload,
                    visibleColumns: currentVisibleColumns,
                    losThreshold: losMovedOn ? currentLos : payload.losThreshold,
                    effectiveLosThreshold: losMovedOn ? currentLos : payload.effectiveLosThreshold,
                    systemDefaultLosThreshold: payload.systemDefaultLosThreshold
                };
                if (state.meetingViewMeta) {
                    if (!losMovedOn) {
                        state.meetingViewMeta.effectiveLosThreshold = payload.effectiveLosThreshold;
                    }
                    state.meetingViewMeta.systemDefaultLosThreshold = payload.systemDefaultLosThreshold;
                }
                state.meetingViewLocalPreferencesDirty = columnsMovedOn || losMovedOn;
            })
            .addCase(saveMeetingViewPreferences.rejected, (state, { meta }) => {
                if (state.currentMeetingViewPreferencesRequestId !== meta.requestId) {
                    return;
                }
                state.meetingViewPreferencesSaving = false;
                state.currentMeetingViewPreferencesRequestId = null;
            })
            .addCase(fetchProgressNotesPatients.pending, (state, { meta }) => {
                state.progressNotesLoading = true;
                state.progressNotesError = null;
                state.currentProgressNotesRequestId = meta.requestId;
            })
            .addCase(fetchProgressNotesPatients.fulfilled, (state, { meta, payload }) => {
                if (state.currentProgressNotesRequestId !== meta.requestId) {
                    return;
                }
                state.progressNotesLoading = false;
                state.progressNotesPatients = payload.items;
                state.progressNotesTotal = payload.total;
                state.progressNotesServerPage = payload.page;
                state.progressNotesServerLimit = payload.limit;
                state.progressNotesError = null;
                state.currentProgressNotesRequestId = null;
                clearProgressNoteSessionCache();
            })
            .addCase(fetchProgressNotesPatients.rejected, (state, { error, meta, payload }) => {
                if (state.currentProgressNotesRequestId !== meta.requestId) {
                    return;
                }
                state.progressNotesLoading = false;
                if (error.name === 'AbortError') {
                    state.progressNotesError = null;
                } else {
                    state.progressNotesError = payload as string;
                }
                state.currentProgressNotesRequestId = null;
            })
            .addCase(fetchProgressNotesSelectionPatients.pending, (state, { meta }) => {
                state.progressNotesSelectionLoading = true;
                state.progressNotesSelectionError = null;
                state.currentProgressNotesSelectionRequestId = meta.requestId;
            })
            .addCase(fetchProgressNotesSelectionPatients.fulfilled, (state, { meta, payload }) => {
                if (state.currentProgressNotesSelectionRequestId !== meta.requestId) {
                    return;
                }
                state.progressNotesSelectionLoading = false;
                state.progressNotesSelectionPatients = payload.items;
                state.progressNotesSelectionTotal = payload.total;
                state.progressNotesSelectionServerPage = payload.page;
                state.progressNotesSelectionServerLimit = payload.limit;
                state.progressNotesSelectionError = null;
                state.currentProgressNotesSelectionRequestId = null;
            })
            .addCase(fetchProgressNotesSelectionPatients.rejected, (state, { error, meta, payload }) => {
                if (state.currentProgressNotesSelectionRequestId !== meta.requestId) {
                    return;
                }
                state.progressNotesSelectionLoading = false;
                if (error.name === 'AbortError') {
                    state.progressNotesSelectionError = null;
                } else {
                    state.progressNotesSelectionError = payload as string;
                }
                state.currentProgressNotesSelectionRequestId = null;
            })
            .addCase(fetchPatientById.pending, (state) => {
                state.currentPatientLoading = true;
                state.currentPatientError = null;
            })
            .addCase(fetchPatientById.fulfilled, (state, { payload }) => {
                state.currentPatientLoading = false;
                state.currentPatient = payload;
                state.currentPatientError = null;
            })
            .addCase(fetchPatientById.rejected, (state, { payload }) => {
                state.currentPatientLoading = false;
                state.currentPatientError = payload as string;
            })
            .addCase(fetchUnits.pending, (state) => {
                state.unitsLoading = true;
                state.unitsError = null;
            })
            .addCase(fetchUnits.fulfilled, (state, { payload }) => {
                state.unitsLoading = false;
                state.units = payload;
                state.unitsError = null;
            })
            .addCase(fetchUnits.rejected, (state, { payload }) => {
                state.unitsLoading = false;
                state.unitsError = payload as string;
            })
            .addCase(savePatientLlosNote.fulfilled, (state, { payload }) => {
                const llosCopy = payload.llosCopy ?? payload.llosNote;
                const inList = state.patients.find((patient) => patient.id === payload.patientId);
                if (inList) {
                    inList.llosNote = payload.llosNote;
                    inList.llosCopy = llosCopy;
                    inList.isLlos = payload.isLlos;
                }
                const inMeetingView = state.meetingViewPatients.find((patient) => patient.id === payload.patientId);
                if (inMeetingView) {
                    inMeetingView.llosNote = payload.llosNote;
                    inMeetingView.llosCopy = llosCopy;
                    inMeetingView.isLlos = payload.isLlos;
                }
            })
            .addCase(savePatientStickyNote.fulfilled, (state, { payload }) => {
                const inList = state.patients.find((patient) => patient.id === payload.patientId);
                if (inList) {
                    inList.stickyNote = payload.stickyNote;
                }
                const inMeetingView = state.meetingViewPatients.find((patient) => patient.id === payload.patientId);
                if (inMeetingView) {
                    inMeetingView.stickyNote = payload.stickyNote;
                }
            })
            .addCase(savePatientProgressNote.fulfilled, (state, { payload, meta }) => {
                const patientId = payload.patientId ?? meta.arg.body.patientId;
                const patient = state.progressNotesPatients.find((item) => item.id === patientId);
                if (!patient) {
                    return;
                }

                patient.latestProgressNote = payload;
            })
            .addCase(logout, () => initialState);
    }
});

export const {
    clearPatients,
    clearProgressNotes,
    clearProgressNotesError,
    clearProgressNotesSelection,
    clearProgressNotesSelectionError,
    patchProgressNotesSelectionPatient,
    patchProgressNotesSelectionPatients,
    patchProgressNotesPatientLatestNote,
    clearUnits,
    clearError,
    clearUnitsError,
    clearMeetingView,
    patchMeetingViewVisibleColumns,
    patchMeetingViewLosThreshold,
    markMeetingViewPreferencesSynced,
    clearCurrentPatient,
    markPatientWeekendDischargeSubmitted,
    patchPatientNotes,
    patchPatientAiFeedback
} = patientsSlice.actions;
export default patientsSlice.reducer;
